import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';

import { Page, LayoutSingleColumn, AvatarLarge, UserBadge } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import BuildAWheelSection from './BuildAWheelSection';
import StoreListingsSection from './StoreListingsSection';
import CatalogueSection from './CatalogueSection';
import GoogleReviewsBadge from './GoogleReviewsBadge';
import css from './CompanyProfilePage.module.css';

/* New company ("Återförsäljare") profile layout — matches the
 * approved "Bygg ditt hjul" mockup, built in Wheelplace UI. Gated in
 * ProfilePage.js to users with publicData.userType === 'business';
 * private sellers keep the existing LayoutSideNavigation profile. */
const CompanyProfilePage = props => {
  const {
    scrollingDisabled,
    schemaTitle,
    profileUrl,
    user,
    displayName,
    profileUserId,
    metadata,
    publicData,
    listings,
    catalogueListings = [],
    catalogueNextPage = null,
    catalogueInProgress = false,
    catalogueFilter = {},
    catalogueHasAny = false,
    reviews,
    userShowError,
    queryListingsError,
  } = props;

  const { isVerified, isTopSeller } = metadata || {};

  // Seller-entered links (Länkar tab). Only the website is public here;
  // googleBusinessUrl is admin-facing (Part A) and bookingUrl is order-only.
  const website = publicData?.links?.website;

  // Real rating derived from this seller's provider reviews; falls
  // back to placeholder text when there are none yet.
  const providerReviews = (reviews || []).filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewCount = providerReviews.length;
  const avgRating =
    reviewCount > 0
      ? (
          providerReviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0) / reviewCount
        ).toFixed(1)
      : null;
  const ratingLabel = avgRating ? avgRating.replace('.', ',') : '–';

  const hasError = userShowError || queryListingsError;

  // A ProfilePage needs a `mainEntity` describing whose profile it is —
  // without it Google rejects the rich result. A company seller is an
  // Organization, and the star rating shown in the header above is the same
  // number we can hand to search engines as an aggregateRating.
  const profileSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: schemaTitle,
    mainEntity: {
      '@type': 'Organization',
      name: displayName,
      ...(profileUrl ? { url: profileUrl } : {}),
      ...(website ? { sameAs: [website] } : {}),
      ...(reviewCount > 0
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: Number(avgRating),
              reviewCount,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
    },
  };

  return (
    <Page scrollingDisabled={scrollingDisabled} title={schemaTitle} schema={profileSchema}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.cover} />
        <div className={css.wrap}>
          {/* Company header */}
          <div className={css.profile}>
            <div className={css.avatarWrap}>
              <AvatarLarge className={css.avatar} user={user} disableProfileLink />
            </div>

            <div className={css.headerMain}>
              <div className={css.nameRow}>
                <h1 className={css.name}>{displayName}</h1>
                <UserBadge isVerified={isVerified} isTopSeller={isTopSeller} />
                <span className={`${css.badge} ${css.badgeBlue}`}>Återförsäljare</span>
              </div>
              <div className={css.metaLine}>
                ★ {ratingLabel}
                {reviewCount > 0 ? ` (${reviewCount} recensioner)` : ' (Inga recensioner ännu)'} · 📍{' '}
                {displayName}
              </div>
              <GoogleReviewsBadge profileUserId={profileUserId} />
              <div className={css.metaLineSub}>
                <FormattedMessage id="CompanyProfilePage.responseTime" />
              </div>
              {website ? (
                <a
                  className={css.websiteLink}
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🌐 <FormattedMessage id="CompanyProfilePage.websiteLink" />
                </a>
              ) : null}
            </div>

            {/* TODO(wire): hook up to the inquiry / follow flows. */}
            <div className={css.contactBtns}>
              <button type="button" className={css.btnPrimary}>
                💬 Kontakta
              </button>
              <button type="button" className={css.btnGhost}>
                📞 Ring
              </button>
              <button type="button" className={css.btnGhost}>
                🔖 Följ
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className={css.stats}>
            <div className={css.statCell}>
              <div className={css.statBig}>{listings.length}</div>
              <div className={css.statLabel}>Aktiva annonser</div>
            </div>
            <div className={css.statCell}>
              <div className={css.statBig}>{reviewCount > 0 ? `${ratingLabel} / 5` : '–'}</div>
              <div className={css.statLabel}>Snittbetyg</div>
            </div>
            <div className={css.statCell}>
              <div className={css.statBig}>{reviewCount}</div>
              <div className={css.statLabel}>Recensioner</div>
            </div>
          </div>

          {hasError ? (
            <p className={css.error}>
              <FormattedMessage id="ProfilePage.loadingDataFailed" />
            </p>
          ) : (
            <>
{/* P25009-406 AC-3 — the module reads THIS dealer's stock, so it
                  needs to know whose profile it is on. */}
              {/* profileUserId scopes the catalogue query: "Bygg ditt hjul"
                * shows THIS dealer's supplier assortment, and without the id
                * it has nothing to ask about. */}
              <BuildAWheelSection displayName={displayName} userId={profileUserId} profileUserId={profileUserId} />
              <StoreListingsSection
                listings={listings}
                profileUserId={profileUserId}
                displayName={displayName}
              />
              {/* P25009-410 BR-3 — the dealer's catalogue: stock they have
                * not advertised, so the profile works as their stock list. */}
              <CatalogueSection
                listings={catalogueListings}
                nextPage={catalogueNextPage}
                inProgress={catalogueInProgress}
                filter={catalogueFilter}
                hasAny={catalogueHasAny}
                profileUserId={profileUserId}
              />
            </>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CompanyProfilePage;
